import React from "react";
import { useScrollBlock } from "./useScrollBlock";

const CLOSE_DELAY_MS = 100;

const useDialogState = (
  initState: boolean = false,
): [boolean, () => void, () => void] => {
  const [isOpen, setOpen] = React.useState<boolean>(initState);
  const [blockScroll, allowScroll] = useScrollBlock();

  const allowScrollTimeoutId = React.useRef<number | null>(null);

  // Store the latest scroll functions in refs to avoid stale closures in cleanup
  const allowScrollRef = React.useRef(allowScroll);
  React.useEffect(() => {
    allowScrollRef.current = allowScroll;
  }, [allowScroll]);

  const clearAllowScrollTimeout = React.useCallback(() => {
    if (allowScrollTimeoutId.current != null) {
      window.clearTimeout(allowScrollTimeoutId.current);
      allowScrollTimeoutId.current = null;
    }
  }, []);

  // Single place that owns scroll side-effects.
  React.useEffect(() => {
    // Ensure we never leak a pending timeout.
    clearAllowScrollTimeout();

    if (isOpen) {
      // Opening should block immediately.
      blockScroll();
      return;
    }

    // Closed: schedule delayed allow.
    allowScrollTimeoutId.current = window.setTimeout(() => {
      allowScroll();
      allowScrollTimeoutId.current = null;
    }, CLOSE_DELAY_MS);
  }, [allowScroll, blockScroll, clearAllowScrollTimeout, isOpen]);

  // Always cleanup on unmount only.
  // Using refs to get latest values without adding them as dependencies.
  React.useEffect(() => {
    return () => {
      if (allowScrollTimeoutId.current != null) {
        window.clearTimeout(allowScrollTimeoutId.current);
        allowScrollTimeoutId.current = null;
      }
      allowScrollRef.current();
    };
  }, []);

  const open = React.useCallback(() => {
    // Keep side-effect here because it isn't tied to scroll state.
    window.scrollTo(0, 0);
    setOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
  }, []);

  return [isOpen, open, close];
};

export default useDialogState;
